(function () {
  "use strict";

  const root = document.querySelector("[data-energy-calculator]");
  if (!root || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";
  const config = window.DELLA_ENERGY_CONFIG || {};
  const apiBaseUrl = String(config.apiBaseUrl || "").replace(/\/$/, "");
  const money = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  const moneyExact = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
  const baselineTypeLossPercent = {
    unknown: 0,
    fixed: 8,
    inverter: 0,
  };
  const baselineAgeLossPercent = {
    unknown: 0,
    under_5: 0,
    "5_10": 5,
    "11_15": 10,
    "16_plus": 15,
  };

  const elements = Object.fromEntries([
    "catalogSummary", "productSearch", "productSelect", "productSnapshot",
    "productImage", "productSeries", "productTitle", "productTags",
    "productPrice", "productLink", "zipForm", "zipCode", "findRateButton",
    "rateStatus", "useBillButton", "utilityDetection", "detectedUtilityText",
    "reviewPlansButton", "advancedSettings", "advancedSummary",
    "activeRateValue", "tariffFields", "utilitySelect", "tariffSelect",
    "tariffDetail", "usageTimingField", "usageTiming", "billFields",
    "monthlyKwh", "totalBill", "fixedCharges", "billRateStatus",
    "billProjection", "currentBillValue", "projectedBillValue",
    "projectedBillDifference", "billProjectionNote",
    "manualRateFields", "electricityRate", "hoursPerDay", "hoursOutput",
    "monthsPerYear", "monthsOutput", "loadOutput", "oldSystemType",
    "oldSystemAge", "oldCondition", "capacityBtu", "seer2",
    "baselineSeer2", "baselineAssumption", "efficiencyNotice", "calculationStatus", "annualCost",
    "annualContext", "monthlyCost", "annualKwh", "fiveYearSavings",
    "annualSavings", "selectedBarValue", "baselineBarValue", "selectedBar",
    "baselineBar", "baselineLabel", "savingsContext", "ratedPower",
    "scheduledHours", "fullLoadHours", "rateSource",
  ].map((id) => [id, root.querySelector(`#${id}`)]));

  let products = [];
  let productSummary = null;
  let electricityOptions = null;
  let electricityRateMetadata = null;
  let selectedTariffDetail = null;
  let manualRateValue = 20;
  let catalogSelectionInitialized = false;
  let calculateTimer = 0;
  let billRateTimer = 0;
  let requestSequence = 0;
  let rateLookupSequence = 0;
  let tariffLookupSequence = 0;
  let latestEstimate = null;
  let latestEstimateInput = null;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }

  function initialize() {
    bindEvents();
    syncUsageLabels();
    syncAdvancedSummary();
    updateRateModePanels();
    loadCatalog();
    findElectricityRate();
  }

  function bindEvents() {
    elements.productSearch.addEventListener("input", () => {
      renderProductOptions(elements.productSearch.value);
    });
    elements.productSelect.addEventListener("change", selectProduct);
    elements.zipForm.addEventListener("submit", (event) => {
      event.preventDefault();
      findElectricityRate();
    });
    elements.useBillButton.addEventListener("click", () => {
      elements.advancedSettings.open = true;
      setRateMode("bill");
      window.requestAnimationFrame(() => {
        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth";
        elements.billFields.scrollIntoView({ behavior, block: "center" });
        elements.monthlyKwh.focus({ preventScroll: true });
      });
    });
    root.querySelectorAll('input[name="usageSector"]').forEach((input) => {
      input.addEventListener("change", () => {
        selectedTariffDetail = null;
        if (currentRateMode() === "tariff") setRateMode("auto");
        findElectricityRate();
        syncAdvancedSummary();
      });
    });
    root.querySelectorAll('input[name="rateMode"]').forEach((input) => {
      input.addEventListener("change", handleRateModeChange);
    });
    elements.reviewPlansButton.addEventListener("click", () => {
      elements.advancedSettings.open = true;
      setRateMode("tariff");
      elements.utilitySelect.focus();
    });
    elements.utilitySelect.addEventListener("change", () => {
      selectedTariffDetail = null;
      populateTariffOptions();
      if (currentRateMode() === "tariff") {
        setRateStatus("Select the exact rate plan shown on the customer's bill.", "manual");
      }
    });
    elements.tariffSelect.addEventListener("change", loadSelectedTariff);
    elements.usageTiming.addEventListener("change", () => {
      if (selectedTariffDetail && currentRateMode() === "tariff") {
        applyTariffRate(selectedTariffDetail);
      }
    });
    [elements.monthlyKwh, elements.totalBill, elements.fixedCharges].forEach((input) => {
      input.addEventListener("input", scheduleBillRate);
    });
    elements.electricityRate.addEventListener("input", () => {
      if (currentRateMode() !== "manual") return;
      manualRateValue = Number(elements.electricityRate.value);
      electricityRateMetadata = {
        sourceName: "Manually entered electricity rate",
        period: "current input",
        sourceType: "MANUAL",
      };
      updateActiveRateValue();
      setRateStatus("Using the manually entered electricity rate.", "manual");
      syncAdvancedSummary();
      scheduleCalculation();
    });
    elements.hoursPerDay.addEventListener("input", () => {
      syncUsageLabels();
      scheduleCalculation();
    });
    elements.monthsPerYear.addEventListener("input", () => {
      syncUsageLabels();
      scheduleCalculation();
    });
    root.querySelectorAll('input[name="loadFactor"]').forEach((input) => {
      input.addEventListener("change", () => {
        syncUsageLabels();
        syncAdvancedSummary();
        scheduleCalculation();
      });
    });
    [
      elements.capacityBtu,
      elements.seer2,
      elements.baselineSeer2,
      elements.oldCondition,
      elements.oldSystemType,
      elements.oldSystemAge,
    ].forEach((input) => {
      input.addEventListener("input", () => {
        syncAdvancedSummary();
        scheduleCalculation();
      });
      input.addEventListener("change", () => {
        syncAdvancedSummary();
        scheduleCalculation();
      });
    });
  }

  async function loadCatalog() {
    setCatalogStatus("Loading live products...");
    try {
      const response = await fetch(apiUrl("/api/energy-products"), {
        headers: { Accept: "application/json" },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Unable to load products.");
      products = Array.isArray(payload.products) ? payload.products : [];
      productSummary = payload.summary || null;
      renderProductOptions("");
      const excluded = productSummary ? productSummary.excludedWithoutEfficiency : 0;
      setCatalogStatus(`${products.length} live systems${excluded ? ` / ${excluded} awaiting SEER2` : ""}`);
    } catch (error) {
      products = [];
      renderProductOptions("");
      setCatalogStatus(error.message || "Catalog unavailable", true);
    }
  }

  function renderProductOptions(query) {
    const normalized = String(query || "").trim().toLowerCase();
    const previousValue = elements.productSelect.value;
    const filtered = products.filter((product) => {
      if (!normalized) return true;
      const haystack = [
        product.title,
        product.series,
        product.family,
        product.capacityBtu,
        product.zoneCount ? `${product.zoneCount} zone` : "",
      ].join(" ").toLowerCase();
      return haystack.includes(normalized);
    });

    elements.productSelect.replaceChildren();
    if (!filtered.length) {
      elements.productSelect.append(new Option(
        products.length ? "No products match this search" : "No calculable products available",
        "",
      ));
    }
    filtered.forEach((product) => {
      const efficiency = product.seer2 ? `${product.seer2} SEER2` : "SEER2 missing";
      const capacity = `${Math.round(product.capacityBtu / 1000)}K BTU`;
      elements.productSelect.append(new Option(
        `${product.title} - ${capacity}, ${efficiency}`,
        product.handle,
      ));
    });

    const previousStillVisible = [...elements.productSelect.options]
      .some((option) => option.value === previousValue);
    if (previousStillVisible) {
      elements.productSelect.value = previousValue;
    } else if (!catalogSelectionInitialized && products.length) {
      const preferred = products.find((product) => (
        product.seer2
        && product.zoneCount === 1
        && product.capacityBtu === 12_000
        && !/(cassette|ducted|floor[\s-]*ceiling|ceiling)/i.test(product.title)
      )) || products.find((product) => product.seer2) || products[0];
      elements.productSelect.value = preferred.handle;
      catalogSelectionInitialized = true;
    } else if (filtered.length) {
      elements.productSelect.value = filtered[0].handle;
    } else {
      elements.productSelect.value = "";
    }
    selectProduct();
  }

  function selectProduct() {
    const selected = products.find(
      (product) => product.handle === elements.productSelect.value,
    );
    if (!selected) {
      renderManualProduct();
      scheduleCalculation();
      return;
    }

    elements.capacityBtu.value = selected.capacityBtu || "";
    elements.seer2.value = selected.seer2 || "";
    elements.productSeries.textContent = `${selected.series || selected.family} / live Shopify product`;
    elements.productTitle.textContent = selected.title;
    renderProductImage(selected.imageUrl, selected.title);
    elements.productTags.replaceChildren(
      tag(`${selected.zoneCount} zone${selected.zoneCount === 1 ? "" : "s"}`),
      tag(`${number.format(selected.capacityBtu)} BTU/h`),
      tag(selected.refrigerant === "UNKNOWN" ? "Refrigerant missing" : selected.refrigerant),
      tag(selected.seer2 ? `${selected.seer2} SEER2` : "SEER2 required", !selected.seer2),
    );
    elements.productPrice.textContent = formatPrice(selected.minPrice, selected.maxPrice);
    elements.productLink.href = selected.productUrl;
    elements.productLink.hidden = false;
    elements.efficiencyNotice.textContent = selected.seer2
      ? `SEER2 source: ${selected.efficiencySource === "PRODUCT_RECOGNITION" ? "DELLA product data" : "storefront product title"}. You can refine the assumptions under Advanced.`
      : "This product is waiting for verified efficiency data.";
    scheduleCalculation();
  }

  function renderManualProduct() {
    elements.productSeries.textContent = "Product unavailable";
    elements.productTitle.textContent = "Choose another search term or try again later.";
    elements.productImage.replaceChildren(document.createTextNode("DELLA"));
    elements.productTags.replaceChildren(tag("Editable inputs"), tag("No Shopify snapshot", true));
    elements.productPrice.textContent = "--";
    elements.productLink.hidden = true;
    elements.capacityBtu.value = "";
    elements.seer2.value = "";
    elements.efficiencyNotice.textContent = "Only products with recognized SEER2 data can be calculated.";
  }

  function renderProductImage(source, alt) {
    elements.productImage.replaceChildren();
    if (!source) {
      elements.productImage.append(document.createTextNode("DELLA"));
      return;
    }
    const image = new Image();
    image.src = source;
    image.alt = alt;
    image.loading = "lazy";
    image.addEventListener("error", () => {
      elements.productImage.replaceChildren(document.createTextNode("DELLA"));
    });
    elements.productImage.append(image);
  }

  async function findElectricityRate() {
    const zip = elements.zipCode.value.trim();
    if (!/^\d{5}(?:-\d{4})?$/.test(zip)) {
      setRateStatus("Enter a valid five-digit U.S. ZIP code.", "error");
      return;
    }

    const sequence = ++rateLookupSequence;
    const sector = currentSector();
    elements.findRateButton.disabled = true;
    elements.findRateButton.textContent = "Finding...";
    setRateStatus(`Looking up ${sector} electricity options...`);
    try {
      const response = await fetch(
        apiUrl(`/api/energy-rate?zip=${encodeURIComponent(zip)}&sector=${sector}`),
        { headers: { Accept: "application/json" } },
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Rate lookup failed.");
      if (sequence !== rateLookupSequence) return;

      electricityOptions = payload;
      selectedTariffDetail = null;
      populateUtilityOptions();
      renderUtilityDetection();
      if (currentRateMode() === "auto") applyStateRate();
      else renderLocationStatus();
    } catch (error) {
      if (sequence !== rateLookupSequence) return;
      electricityOptions = null;
      selectedTariffDetail = null;
      elements.utilityDetection.hidden = true;
      populateUtilityOptions();
      setRateStatus(
        `${error.message || "Rate lookup failed."} Enter a bill or manual rate under Advanced.`,
        "error",
      );
      scheduleCalculation();
    } finally {
      if (sequence === rateLookupSequence) {
        elements.findRateButton.disabled = false;
        elements.findRateButton.textContent = "Find rate";
      }
    }
  }

  function renderUtilityDetection() {
    const utilities = electricityOptions && Array.isArray(electricityOptions.utilities)
      ? electricityOptions.utilities
      : [];
    elements.utilityDetection.hidden = utilities.length === 0;
    if (!utilities.length) return;
    const names = utilities.slice(0, 2).map((utility) => utility.name);
    elements.detectedUtilityText.textContent = utilities.length > 2
      ? `${names.join(", ")} +${utilities.length - 2} more`
      : names.join(", ");
  }

  function populateUtilityOptions() {
    const utilities = electricityOptions && Array.isArray(electricityOptions.utilities)
      ? electricityOptions.utilities
      : [];
    elements.utilitySelect.replaceChildren();
    if (!utilities.length) {
      elements.utilitySelect.append(new Option("No utility plans detected", ""));
      elements.utilitySelect.disabled = true;
      populateTariffOptions();
      return;
    }

    elements.utilitySelect.disabled = false;
    if (utilities.length > 1) {
      elements.utilitySelect.append(new Option("Select the utility on the bill", ""));
    }
    utilities.forEach((utility) => {
      elements.utilitySelect.append(new Option(
        `${utility.name} (${utility.tariffCount} plans)`,
        utility.name,
      ));
    });
    if (utilities.length === 1) elements.utilitySelect.value = utilities[0].name;
    populateTariffOptions();
  }

  function populateTariffOptions() {
    const utilityName = elements.utilitySelect.value;
    const tariffs = electricityOptions && Array.isArray(electricityOptions.tariffs)
      ? electricityOptions.tariffs.filter((tariff) => tariff.utility === utilityName)
      : [];
    elements.tariffSelect.replaceChildren();
    elements.tariffSelect.append(new Option(
      tariffs.length ? "Select the exact plan on the bill" : "Select a utility first",
      "",
    ));
    tariffs.forEach((tariff) => {
      const flags = [
        tariff.isDefault ? "OpenEI default candidate" : "",
        tariff.specialized ? "special program" : "",
      ].filter(Boolean);
      const service = tariff.serviceType && !/bundled/i.test(tariff.serviceType)
        ? ` - ${tariff.serviceType}`
        : "";
      elements.tariffSelect.append(new Option(
        `${tariff.name}${service}${flags.length ? ` (${flags.join(", ")})` : ""}`,
        tariff.label,
      ));
    });
    elements.tariffSelect.disabled = tariffs.length === 0;
    elements.usageTimingField.hidden = true;
    renderTariffPrompt();
  }

  async function loadSelectedTariff() {
    const label = elements.tariffSelect.value;
    selectedTariffDetail = null;
    elements.usageTimingField.hidden = true;
    if (!label) {
      renderTariffPrompt();
      if (currentRateMode() === "tariff") renderLocationStatus();
      return;
    }

    const sequence = ++tariffLookupSequence;
    elements.tariffSelect.disabled = true;
    elements.tariffDetail.textContent = "Loading tariff structure...";
    try {
      const response = await fetch(
        apiUrl(`/api/energy-tariff?label=${encodeURIComponent(label)}`),
        { headers: { Accept: "application/json" } },
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Tariff lookup failed.");
      if (sequence !== tariffLookupSequence) return;
      selectedTariffDetail = payload.tariff;
      renderTariffDetail(selectedTariffDetail);
      elements.usageTimingField.hidden = selectedTariffDetail.rateType !== "TIME_OF_USE";
      if (currentRateMode() === "tariff") applyTariffRate(selectedTariffDetail);
    } catch (error) {
      if (sequence !== tariffLookupSequence) return;
      elements.tariffDetail.textContent = error.message || "Tariff details are unavailable.";
      setRateStatus("The selected tariff could not be applied. State average remains available.", "error");
    } finally {
      if (sequence === tariffLookupSequence) elements.tariffSelect.disabled = false;
    }
  }

  function renderTariffPrompt() {
    elements.tariffDetail.replaceChildren(document.createTextNode(
      "Select the exact plan shown on the customer's bill. A detected utility is not enough to identify a tariff.",
    ));
  }

  function renderTariffDetail(tariff) {
    const typeLabels = {
      FLAT: "Flat energy rate",
      TIERED: "Tiered energy rate",
      TIME_OF_USE: "Time-of-use rate",
    };
    const facts = document.createElement("div");
    facts.className = "tariff-facts";
    facts.append(
      tariffFact("Plan", tariff.name),
      tariffFact("Structure", typeLabels[tariff.rateType] || tariff.rateType),
      tariffFact(
        "Listed energy rate",
        tariff.minimumRateCents === tariff.maximumRateCents
          ? `${formatCents(tariff.minimumRateCents)} / kWh`
          : `${formatCents(tariff.minimumRateCents)}-${formatCents(tariff.maximumRateCents)} / kWh`,
      ),
      tariffFact(
        "Fixed charge",
        Number.isFinite(tariff.fixedMonthlyCharge)
          ? `${moneyExact.format(tariff.fixedMonthlyCharge)} / month`
          : "Not listed",
      ),
      tariffFact("Effective", tariff.startDate || "Date not listed"),
    );
    const warning = document.createElement("p");
    warning.textContent = tariff.warning;
    elements.tariffDetail.replaceChildren(facts, warning);
  }

  function tariffFact(label, value) {
    const item = document.createElement("div");
    const term = document.createElement("span");
    const detail = document.createElement("strong");
    term.textContent = label;
    detail.textContent = value;
    item.append(term, detail);
    return item;
  }

  function handleRateModeChange() {
    updateRateModePanels();
    const mode = currentRateMode();
    if (mode === "auto") applyStateRate();
    if (mode === "tariff") {
      if (selectedTariffDetail) applyTariffRate(selectedTariffDetail);
      else renderLocationStatus();
    }
    if (mode === "bill") scheduleBillRate();
    if (mode === "manual") applyManualRate();
    syncAdvancedSummary();
    renderBillProjection(latestEstimate, latestEstimateInput);
  }

  function setRateMode(mode) {
    const input = root.querySelector(`input[name="rateMode"][value="${mode}"]`);
    if (!input) return;
    input.checked = true;
    handleRateModeChange();
  }

  function updateRateModePanels() {
    const mode = currentRateMode();
    elements.tariffFields.hidden = mode !== "tariff";
    elements.billFields.hidden = mode !== "bill";
    elements.manualRateFields.hidden = mode !== "manual";
    elements.useBillButton.setAttribute("aria-expanded", String(mode === "bill"));
  }

  function applyStateRate() {
    const rate = electricityOptions && electricityOptions.rate;
    if (!rate) {
      setRateStatus("Find a ZIP code or choose another rate source under Advanced.", "error");
      return;
    }
    applyRate(rate.rateCentsPerKwh, {
      ...rate,
      sourceType: "EIA",
    });
    renderLocationStatus();
  }

  function applyTariffRate(tariff) {
    let rate = tariff.suggestedRateCents;
    if (tariff.rateType === "TIME_OF_USE") {
      if (elements.usageTiming.value === "off_peak") rate = tariff.minimumRateCents;
      if (elements.usageTiming.value === "peak") rate = tariff.maximumRateCents;
    }
    applyRate(rate, {
      sourceName: `${tariff.utility} - ${tariff.name}`,
      period: tariff.startDate || "current tariff",
      sourceType: "TARIFF",
      rateType: tariff.rateType,
    });
    setRateStatus(
      `${formatCents(rate)}/kWh applied from the selected ${tariff.rateType.toLowerCase().replaceAll("_", " ")} plan. ${tariff.warning}`,
      "manual",
    );
  }

  function applyManualRate() {
    if (Number.isFinite(manualRateValue) && manualRateValue > 0) {
      applyRate(manualRateValue, {
        sourceName: "Manually entered electricity rate",
        period: "current input",
        sourceType: "MANUAL",
      });
    }
    setRateStatus("Using the manually entered electricity rate.", "manual");
  }

  function scheduleBillRate() {
    window.clearTimeout(billRateTimer);
    billRateTimer = window.setTimeout(deriveBillRate, 220);
  }

  async function deriveBillRate() {
    if (currentRateMode() !== "bill") return;
    const monthlyKwh = Number(elements.monthlyKwh.value);
    const totalBill = Number(elements.totalBill.value);
    const fixedCharges = Number(elements.fixedCharges.value || 0);
    if (!(monthlyKwh > 0) || !(totalBill > 0)) {
      elements.billRateStatus.textContent = "Enter monthly kWh and total bill to calculate the blended variable rate.";
      elements.billProjection.hidden = true;
      setRateStatus("Bill rate is waiting for monthly kWh and total bill.", "manual");
      return;
    }

    try {
      const response = await fetch(apiUrl("/api/energy-bill-rate"), {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8", Accept: "application/json" },
        body: JSON.stringify({ monthlyKwh, totalBill, fixedCharges }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Bill rate could not be calculated.");
      applyRate(payload.rateCentsPerKwh, {
        sourceName: "Customer bill blended variable rate",
        period: "bill snapshot",
        sourceType: "BILL",
      });
      elements.billRateStatus.textContent = `${formatCents(payload.rateCentsPerKwh)}/kWh from ${moneyExact.format(payload.variableCharges)} in variable charges. ${billBenchmarkDescription(payload.rateCentsPerKwh)}`;
      setRateStatus("Using the customer's bill-derived blended variable rate.", "manual");
    } catch (error) {
      elements.billProjection.hidden = true;
      elements.billRateStatus.textContent = error.message || "Bill rate could not be calculated.";
      setRateStatus(elements.billRateStatus.textContent, "error");
    }
  }

  function billBenchmarkDescription(billRate) {
    const benchmark = Number(electricityOptions && electricityOptions.rate
      ? electricityOptions.rate.rateCentsPerKwh
      : 0);
    if (!(benchmark > 0)) {
      return "Your bill rate is used directly; no local API benchmark is currently available.";
    }
    const differencePercent = (billRate - benchmark) / benchmark * 100;
    const comparison = Math.abs(differencePercent) < 2
      ? "about the same as"
      : `${Math.abs(differencePercent).toFixed(0)}% ${differencePercent > 0 ? "above" : "below"}`;
    return `The local API benchmark is ${formatCents(benchmark)}/kWh; your bill is ${comparison} that benchmark. Your bill remains the calculation source.`;
  }

  function applyRate(rate, metadata) {
    elements.electricityRate.value = Number(rate).toFixed(2);
    electricityRateMetadata = metadata;
    updateActiveRateValue();
    syncAdvancedSummary();
    scheduleCalculation();
  }

  function renderLocationStatus() {
    if (!electricityOptions || !electricityOptions.rate) return;
    const rate = electricityOptions.rate;
    const location = [rate.city, rate.state].filter(Boolean).join(", ");
    const utilityCount = Array.isArray(electricityOptions.utilities)
      ? electricityOptions.utilities.length
      : 0;
    const sector = rate.sector === "COMMERCIAL" ? "commercial" : "residential";
    const commercialNote = sector === "commercial"
      ? " This is a statewide blended commercial benchmark, not a quoted business tariff. Commercial average cents/kWh can be lower than residential; site-specific demand, fixed, and time-of-use charges are not modeled separately."
      : "";
    const planNote = utilityCount
      ? ` ${utilityCount} local utility option${utilityCount === 1 ? "" : "s"} detected; select the exact plan under Advanced.`
      : ` ${electricityOptions.utilityLookupMessage || "No local tariff was detected."}`;
    setRateStatus(
      `${location}: ${formatCents(rate.rateCentsPerKwh)}/kWh, EIA ${sector} state average for ${formatPeriod(rate.period)}.${planNote}${commercialNote}`,
    );
  }

  function scheduleCalculation() {
    window.clearTimeout(calculateTimer);
    calculateTimer = window.setTimeout(calculate, 120);
  }

  async function calculate() {
    const input = getCalculationInput();
    const validationError = validateInput(input);
    if (validationError) {
      setCalculationStatus(validationError, true);
      clearResults();
      return;
    }

    const sequence = ++requestSequence;
    setCalculationStatus("Calculating");
    try {
      const response = await fetch(apiUrl("/api/energy-estimate"), {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8", Accept: "application/json" },
        body: JSON.stringify(input),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Calculation failed.");
      if (sequence !== requestSequence) return;
      renderEstimate(payload.estimate, input);
      setCalculationStatus("Estimate ready");
    } catch (error) {
      if (sequence !== requestSequence) return;
      clearResults();
      setCalculationStatus(error.message || "Calculation failed", true);
    }
  }

  function getCalculationInput() {
    const selectedLoad = root.querySelector('input[name="loadFactor"]:checked');
    const baselineAdjustment = getBaselineAdjustment();
    return {
      capacityBtu: Number(elements.capacityBtu.value),
      seer2: Number(elements.seer2.value),
      hoursPerDay: Number(elements.hoursPerDay.value),
      monthsPerYear: Number(elements.monthsPerYear.value),
      loadFactor: Number(selectedLoad ? selectedLoad.value : 0.65),
      electricityRateCents: Number(elements.electricityRate.value),
      baselineSeer2: Number(elements.baselineSeer2.value),
      baselineEfficiencyLossPercent: baselineAdjustment.combinedLossPercent,
    };
  }

  function getBaselineAdjustment() {
    const typeKey = elements.oldSystemType.value || "unknown";
    const ageKey = elements.oldSystemAge.value || "unknown";
    const typeLoss = baselineTypeLossPercent[typeKey] || 0;
    const ageLoss = baselineAgeLossPercent[ageKey] || 0;
    const conditionLoss = Number(elements.oldCondition.value || 0);
    const retainedEfficiency = [typeLoss, ageLoss, conditionLoss]
      .reduce((retained, loss) => retained * (1 - loss / 100), 1);
    const combinedLossPercent = Math.min(45, Math.max(0, (1 - retainedEfficiency) * 100));
    return {
      typeLoss,
      ageLoss,
      conditionLoss,
      combinedLossPercent: Math.round(combinedLossPercent * 10) / 10,
    };
  }

  function validateInput(input) {
    if (!(input.capacityBtu >= 6000)) return "Enter rated BTU/h";
    if (!(input.seer2 >= 8 && input.seer2 <= 50)) return "Enter product SEER2";
    if (!(input.electricityRateCents > 0)) return "Enter electricity rate";
    if (!(input.baselineSeer2 >= 8 && input.baselineSeer2 <= 50)) return "Check baseline SEER2";
    return "";
  }

  function renderEstimate(estimate, input) {
    latestEstimate = estimate;
    latestEstimateInput = input;
    const sector = currentSector();
    const effectiveLabel = input.baselineEfficiencyLossPercent > 0
      ? `${estimate.effectiveBaselineSeer2} effective SEER2 (${input.baselineSeer2} less ${input.baselineEfficiencyLossPercent}%)`
      : `${input.baselineSeer2} SEER2 baseline`;
    elements.annualCost.textContent = money.format(estimate.annualCost);
    elements.annualContext.textContent = `${input.hoursPerDay} hours/day for ${input.monthsPerYear} months at ${Math.round(input.loadFactor * 100)}% average load, ${sector} service.${sector === "commercial" ? " Uses a statewide blended benchmark; site-specific business charges are not modeled separately." : ""}`;
    elements.monthlyCost.textContent = money.format(estimate.monthlyInSeasonCost);
    elements.annualKwh.textContent = `${number.format(estimate.annualKwh)} kWh`;
    elements.fiveYearSavings.textContent = signedMoney(estimate.fiveYearSavings);
    elements.annualSavings.textContent = `${signedMoney(estimate.annualSavings)} / year`;
    elements.selectedBarValue.textContent = moneyExact.format(estimate.annualCost);
    elements.baselineBarValue.textContent = moneyExact.format(estimate.baselineAnnualCost);
    elements.baselineLabel.textContent = effectiveLabel;

    const maximumCost = Math.max(estimate.annualCost, estimate.baselineAnnualCost, 1);
    elements.selectedBar.style.width = `${Math.max(3, estimate.annualCost / maximumCost * 100)}%`;
    elements.baselineBar.style.width = `${Math.max(3, estimate.baselineAnnualCost / maximumCost * 100)}%`;

    if (estimate.annualSavings >= 0) {
      elements.savingsContext.textContent = `${input.seer2} SEER2 uses about ${estimate.savingsPercent}% less cooling electricity than the ${effectiveLabel} comparison under the same assumptions.`;
    } else {
      elements.savingsContext.textContent = `${input.seer2} SEER2 costs about ${money.format(Math.abs(estimate.annualSavings))} more per year than the ${effectiveLabel} comparison under the same assumptions.`;
    }

    elements.ratedPower.textContent = `${number.format(estimate.ratedPowerKw)} kW at rated seasonal efficiency`;
    elements.scheduledHours.textContent = `${number.format(estimate.scheduledHours)} h/year`;
    elements.fullLoadHours.textContent = `${number.format(estimate.equivalentFullLoadHours)} h/year`;
    elements.rateSource.textContent = electricityRateMetadata
      ? `${electricityRateMetadata.sourceName}, ${formatPeriod(electricityRateMetadata.period)}`
      : "Electricity rate source not identified";
    renderBillProjection(estimate, input);
  }

  function renderBillProjection(estimate, input) {
    const monthlyKwh = Number(elements.monthlyKwh.value);
    const totalBill = Number(elements.totalBill.value);
    const fixedCharges = Number(elements.fixedCharges.value || 0);
    const billRate = Number(elements.electricityRate.value);
    const valid = currentRateMode() === "bill"
      && electricityRateMetadata
      && electricityRateMetadata.sourceType === "BILL"
      && estimate
      && input
      && monthlyKwh > 0
      && totalBill > fixedCharges
      && input.monthsPerYear > 0
      && billRate > 0;
    if (!valid) {
      elements.billProjection.hidden = true;
      return;
    }

    const baselineMonthlyKwh = estimate.baselineAnnualKwh / input.monthsPerYear;
    const selectedMonthlyKwh = estimate.annualKwh / input.monthsPerYear;
    const calibration = baselineMonthlyKwh > monthlyKwh
      ? monthlyKwh / baselineMonthlyKwh
      : 1;
    const monthlyKwhDifference = (baselineMonthlyKwh - selectedMonthlyKwh) * calibration;
    const projectedBill = Math.max(
      fixedCharges,
      totalBill - monthlyKwhDifference * billRate / 100,
    );
    const billDifference = totalBill - projectedBill;
    const direction = monthlyKwhDifference >= 0 ? "lower" : "higher";
    const calibrationNote = calibration < 0.999
      ? " The HVAC usage model was scaled so it does not exceed the bill's total kWh."
      : "";

    elements.currentBillValue.textContent = moneyExact.format(totalBill);
    elements.projectedBillValue.textContent = moneyExact.format(projectedBill);
    elements.projectedBillDifference.textContent = billDifference >= 0
      ? `Save ${moneyExact.format(billDifference)}`
      : `${moneyExact.format(Math.abs(billDifference))} more`;
    elements.billProjectionNote.textContent = `Based on a similar cooling month and ${number.format(Math.abs(monthlyKwhDifference))} kWh ${direction} modeled HVAC use. Other electricity use and fixed charges remain unchanged.${calibrationNote}`;
    elements.billProjection.hidden = false;
  }

  function clearResults() {
    latestEstimate = null;
    latestEstimateInput = null;
    elements.billProjection.hidden = true;
    [
      elements.annualCost,
      elements.monthlyCost,
      elements.annualKwh,
      elements.fiveYearSavings,
      elements.annualSavings,
      elements.selectedBarValue,
      elements.baselineBarValue,
      elements.ratedPower,
      elements.scheduledHours,
      elements.fullLoadHours,
    ].forEach((element) => { element.textContent = "--"; });
    elements.selectedBar.style.width = "0";
    elements.baselineBar.style.width = "0";
    elements.annualContext.textContent = "Enter valid assumptions to calculate.";
    elements.savingsContext.textContent = "Savings appear after calculation.";
  }

  function syncUsageLabels() {
    elements.hoursOutput.textContent = `${elements.hoursPerDay.value} hours`;
    elements.monthsOutput.textContent = `${elements.monthsPerYear.value} months`;
    const selectedLoad = root.querySelector('input[name="loadFactor"]:checked');
    elements.loadOutput.textContent = selectedLoad
      ? `${Math.round(Number(selectedLoad.value) * 100)}%`
      : "65%";
  }

  function syncAdvancedSummary() {
    const selectedLoad = root.querySelector('input[name="loadFactor"]:checked');
    const load = selectedLoad ? Math.round(Number(selectedLoad.value) * 100) : 65;
    const sourceLabels = {
      auto: "State average",
      tariff: selectedTariffDetail ? "Selected utility plan" : "Utility plan pending",
      bill: electricityRateMetadata && electricityRateMetadata.sourceType === "BILL"
        ? "Bill-derived rate"
        : "Bill details pending",
      manual: "Manual rate",
    };
    const adjustment = getBaselineAdjustment();
    const loss = adjustment.combinedLossPercent;
    const baseline = Number(elements.baselineSeer2.value || 14.3);
    const effective = baseline * (1 - loss / 100);
    elements.advancedSummary.textContent = `${sourceLabels[currentRateMode()]} / ${load}% load / ${effective.toFixed(1)} effective SEER2 baseline`;
    const selectedDetails = elements.oldSystemType.value !== "unknown"
      || elements.oldSystemAge.value !== "unknown"
      || adjustment.conditionLoss > 0;
    elements.baselineAssumption.textContent = selectedDetails
      ? `Planning adjustment: system type ${formatPercent(adjustment.typeLoss)}, age ${formatPercent(adjustment.ageLoss)}, condition ${formatPercent(adjustment.conditionLoss)} = ${formatPercent(adjustment.combinedLossPercent)} combined loss. Effective comparison efficiency: ${effective.toFixed(1)} SEER2.`
      : "No automatic type, age or condition adjustment is applied. Select the current system details to refine the comparison.";
    updateActiveRateValue();
  }

  function formatPercent(value) {
    return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;
  }

  function updateActiveRateValue() {
    const rate = Number(elements.electricityRate.value);
    elements.activeRateValue.textContent = rate > 0 ? `${formatCents(rate)} / kWh` : "--";
  }

  function currentSector() {
    return root.querySelector('input[name="usageSector"]:checked')?.value || "residential";
  }

  function currentRateMode() {
    return root.querySelector('input[name="rateMode"]:checked')?.value || "auto";
  }

  function setCatalogStatus(message, error) {
    elements.catalogSummary.textContent = message;
    elements.catalogSummary.classList.toggle("error", Boolean(error));
  }

  function setRateStatus(message, state) {
    elements.rateStatus.textContent = message;
    elements.rateStatus.className = `energy-rate-status${state ? ` ${state}` : ""}`;
  }

  function setCalculationStatus(message, error) {
    elements.calculationStatus.textContent = message;
    elements.calculationStatus.classList.toggle("error", Boolean(error));
  }

  function tag(label, missing) {
    const span = document.createElement("span");
    span.textContent = label;
    if (missing) span.classList.add("missing");
    return span;
  }

  function formatPrice(minimum, maximum) {
    if (!Number.isFinite(minimum)) return "Price unavailable";
    if (Number.isFinite(maximum) && maximum !== minimum) {
      return `${moneyExact.format(minimum)} - ${moneyExact.format(maximum)}`;
    }
    return moneyExact.format(minimum);
  }

  function signedMoney(value) {
    const amount = money.format(Math.abs(value));
    return value >= 0 ? amount : `+${amount} cost`;
  }

  function formatCents(value) {
    return `${Number(value).toFixed(2)} cents`;
  }

  function formatPeriod(period) {
    const match = String(period || "").match(/^(\d{4})-(\d{2})$/);
    if (!match) return period || "latest period";
    const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(date);
  }

  function apiUrl(path) {
    return `${apiBaseUrl}${path}`;
  }
})();
